import { Injectable, computed, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { EventApiService } from './event-api.service';
import { Event, EventResponse } from 'src/app/models/core/event.model';
import { EventReportApiService } from '../event-reports/event-report-api.service';
import { formatCurrency } from 'src/app/utils/currency-format';

@Injectable({
  providedIn: 'root'
})
export class EventSellerService {

  constructor(
    public api: EventApiService,
    public eventReportApi: EventReportApiService,
  ) { }

  readonly upcomingEvents = signal<Event[]>([]);
  readonly pastEvents = signal<Event[]>([]);
  readonly inactiveEvents = signal<Event[]>([]);

  readonly selectedTab = signal<'active' | 'ended' | 'inactive'>('active');
  readonly filterByName = signal('');
  readonly filterByBusiness = signal<number | null>(null);
  readonly currentPage = signal(1);
  readonly totalPages = signal(1);

  private readonly totalPagesByTab = {
    active: 1,
    ended: 1,
    inactive: 1
  };

  // Loading states for each tab
  readonly activeTabLoading = signal(false);
  readonly endedTabLoading = signal(false);
  readonly inactiveTabLoading = signal(false);

  readonly fullLoading = signal(false);
  readonly partialLoading = signal(false);
  readonly loadingMore = signal(false);

  // Computed to get the loading state of the current tab
  readonly currentTabLoading = computed(() => {
    switch (this.selectedTab()) {
      case 'active': return this.activeTabLoading();
      case 'ended': return this.endedTabLoading();
      case 'inactive': return this.inactiveTabLoading();
    }
  });

  readonly currentEvents = computed(() => {
    switch (this.selectedTab()) {
      case 'active': return this.upcomingEvents();
      case 'ended': return this.pastEvents();
      case 'inactive': return this.inactiveEvents();
    }
  });

  readonly totalPagesForCurrentTab = computed(() => {
    return this.totalPagesByTab[this.selectedTab()];
  });

  readonly event = signal<Event | null>(null);
  
  readonly canViewAssistants = computed(() => {
    const currentEvent = this.event();
    return !!currentEvent?.view_assistants;
  });
  readonly totalTickets = signal<number>(0);
  readonly totalSales = signal<number>(0);
  readonly totalEcommerceAmount = signal<number>(0);
  readonly totalDirectAmount = signal<number>(0);
  readonly kpisLoading = signal(false);

  readonly formattedTotalSoldAmount = computed(() =>
    formatCurrency(this.totalSales())
  );

  readonly formattedTotalEcommerceAmount = computed(() =>
    formatCurrency(this.totalEcommerceAmount())
  );

  readonly formattedTotalDirectAmount = computed(() =>
    formatCurrency(this.totalDirectAmount())
  );

  // smart cache system
  private readonly CACHE_DURATION = 5 * 60 * 1000;
  private currentEventId: number | null = null;
  private readonly lastFetchTimes = {
    active: 0,
    ended: 0,
    inactive: 0
  };

  // method to check if the cache is valid
  isCacheValid(tab: 'active' | 'ended' | 'inactive'): boolean {
    const now = Date.now();
    const lastFetch = this.lastFetchTimes[tab];
    const hasData = this.getEventsForTab(tab).length > 0;
    return hasData && (now - lastFetch) < this.CACHE_DURATION;
  }

  private getEventsForTab(tab: 'active' | 'ended' | 'inactive'): Event[] {
    switch (tab) {
      case 'active': return this.upcomingEvents();
      case 'ended': return this.pastEvents();
      case 'inactive': return this.inactiveEvents();
    }
  }

  // Method to check if any tab is loading
  isAnyTabLoading(): boolean {
    return this.activeTabLoading() || this.endedTabLoading() || this.inactiveTabLoading();
  }

  // Method to get the loading state of a specific tab
  getTabLoadingState(tab: 'active' | 'ended' | 'inactive'): boolean {
    switch (tab) {
      case 'active': return this.activeTabLoading();
      case 'ended': return this.endedTabLoading();
      case 'inactive': return this.inactiveTabLoading();
    }
  }

  // Method to set the loading state of a specific tab
  private setTabLoadingState(tab: 'active' | 'ended' | 'inactive', loading: boolean) {
    switch (tab) {
      case 'active':
        this.activeTabLoading.set(loading);
        break;
      case 'ended':
        this.endedTabLoading.set(loading);
        break;
      case 'inactive':
        this.inactiveTabLoading.set(loading);
        break;
    }
  }

  // === Event change detection ===
  private handleEventChange(newEventId: number) {
    if (this.currentEventId !== null && this.currentEventId !== newEventId) {
      this.clearEventData();
    }
    this.currentEventId = newEventId;
  }

  private clearEventData() {
    this.event.set(null);
    this.totalTickets.set(0);
    this.totalSales.set(0);
    this.totalEcommerceAmount.set(0);
    this.totalDirectAmount.set(0);
  }

  // method to load all tabs in parallel
  loadAllTabsInParallel(type: 'full' | 'partial' = 'full', onComplete?: () => void, forceRefresh: boolean = false) {
    const name = this.filterByName();
    const business = this.filterByBusiness() ?? undefined;
    const page = 1;
    const currentTab = this.selectedTab();

    // Check if we need to load any tabs
    const tabsToLoad: ('active' | 'ended' | 'inactive')[] = [];

    // Only load tabs that are not loading and that need to be loaded
    if ((forceRefresh || !this.isCacheValid('active')) && !this.getTabLoadingState('active')) {
      tabsToLoad.push('active');
    }
    if ((forceRefresh || !this.isCacheValid('ended')) && !this.getTabLoadingState('ended')) {
      tabsToLoad.push('ended');
    }
    if ((forceRefresh || !this.isCacheValid('inactive')) && !this.getTabLoadingState('inactive')) {
      tabsToLoad.push('inactive');
    }

    // If all tabs are cached o already loading, just complete
    if (tabsToLoad.length === 0) {
      onComplete?.();
      return;
    }

    // Order tabs for sequential loading: active -> ended -> inactive
    const tabOrder: ('active' | 'ended' | 'inactive')[] = ['active', 'ended', 'inactive'];
    tabsToLoad.sort((a, b) => tabOrder.indexOf(a) - tabOrder.indexOf(b));

    // Set loading state based on whether we have any data
    const hasAnyData = this.upcomingEvents().length > 0 ||
      this.pastEvents().length > 0 ||
      this.inactiveEvents().length > 0;

    // Only use fullLoading if it is the first load and there is no data
    this.fullLoading.set(type === 'full' && !hasAnyData && tabsToLoad.length === 3);
    this.partialLoading.set(type === 'partial');

    // Set individual tab loading states
    tabsToLoad.forEach(tab => {
      this.setTabLoadingState(tab, true);
    });

    // Load tabs sequentially instead of in parallel
    this.loadTabsSequentially(tabsToLoad, name, business, page, onComplete);
  }

  // Method to load tabs sequentially
  private loadTabsSequentially(
    tabsToLoad: ('active' | 'ended' | 'inactive')[],
    name: string,
    business: number | undefined,
    page: number,
    onComplete?: () => void
  ) {
    if (tabsToLoad.length === 0) {
      this.fullLoading.set(false);
      this.partialLoading.set(false);
      onComplete?.();
      return;
    }

    const currentTab = tabsToLoad.shift()!;
    let request$;

    switch (currentTab) {
      case 'active':
        request$ = this.api.getUpcomingEventsBySeller(page, 12, name, business);
        break;
      case 'ended':
        request$ = this.api.getPastEventsBySeller(page, 12, name, business);
        break;
      case 'inactive':
        request$ = this.api.getInactiveEventsBySeller(page, 12, name, business);
        break;
    }

    request$.subscribe({
      next: (res: EventResponse) => {
        // Update data of the current tab
        switch (currentTab) {
          case 'active':
            this.upcomingEvents.set(res.data);
            this.lastFetchTimes.active = Date.now();
            this.totalPagesByTab.active = res.meta?.last_page ?? 1;
            break;
          case 'ended':
            this.pastEvents.set(res.data);
            this.lastFetchTimes.ended = Date.now();
            this.totalPagesByTab.ended = res.meta?.last_page ?? 1;
            break;
          case 'inactive':
            this.inactiveEvents.set(res.data);
            this.lastFetchTimes.inactive = Date.now();
            this.totalPagesByTab.inactive = res.meta?.last_page ?? 1;
            break;
        }

        // Reset loading state del tab actual 
        this.setTabLoadingState(currentTab, false);
        this.totalPages.set(this.totalPagesByTab[this.selectedTab()]);

        // Load the next tab after a small delay to show progress
        setTimeout(() => {
          this.loadTabsSequentially(tabsToLoad, name, business, page, onComplete);
        }, 500); // 500ms de delay entre tabs
      },
      error: () => {
        // Reset loading state of the current tab in case of error
        this.setTabLoadingState(currentTab, false);

        // Continue with the next tab even if there is an error 
        setTimeout(() => {
          this.loadTabsSequentially(tabsToLoad, name, business, page, onComplete);
        }, 500);
      }
    });
  }

  forceRefreshAllTabs(onComplete?: () => void) {
    const name = this.filterByName();
    const business = this.filterByBusiness() ?? undefined;
    const page = 1;

    this.fullLoading.set(true);
    this.partialLoading.set(false);

    // Set all tabs to loading
    this.setTabLoadingState('active', true);
    this.setTabLoadingState('ended', true);
    this.setTabLoadingState('inactive', true);

    const requests: { [key: string]: any } = {
      active: this.api.getUpcomingEventsBySeller(page, 12, name, business),
      ended: this.api.getPastEventsBySeller(page, 12, name, business),
      inactive: this.api.getInactiveEventsBySeller(page, 12, name, business)
    };

    forkJoin(requests).subscribe({
      next: (responses: any) => {

        this.upcomingEvents.set(responses['active'].data);
        this.lastFetchTimes.active = Date.now();
        this.totalPagesByTab.active = responses['active'].meta?.last_page ?? 1;
        this.setTabLoadingState('active', false);

        this.pastEvents.set(responses['ended'].data);
        this.lastFetchTimes.ended = Date.now();
        this.totalPagesByTab.ended = responses['ended'].meta?.last_page ?? 1;
        this.setTabLoadingState('ended', false);

        this.inactiveEvents.set(responses['inactive'].data);
        this.lastFetchTimes.inactive = Date.now();
        this.totalPagesByTab.inactive = responses['inactive'].meta?.last_page ?? 1;
        this.setTabLoadingState('inactive', false);

        this.totalPages.set(this.totalPagesByTab[this.selectedTab()]);

        this.fullLoading.set(false);
        this.partialLoading.set(false);
        onComplete?.();
      },
      error: () => {
        // Reset all loading states on error
        this.setTabLoadingState('active', false);
        this.setTabLoadingState('ended', false);
        this.setTabLoadingState('inactive', false);
        this.fullLoading.set(false);
        this.partialLoading.set(false);
        onComplete?.();
      }
    });
  }

  setSelectedTab(tab: 'active' | 'ended' | 'inactive', type: 'full' | 'partial' = 'partial') {
    this.selectedTab.set(tab);
    this.currentPage.set(1);

    this.totalPages.set(this.totalPagesByTab[tab]);

    // If the cache is valid, we don't need to load
    if (this.isCacheValid(tab)) {
      // Ensure that it is not shown as loading if there is data
      this.setTabLoadingState(tab, false);
      return;
    }

    // If the tab is already loading, only show skeleton without making a new request
    if (this.getTabLoadingState(tab)) {
      return;
    }

    // Verificar si ya hay datos para este tab  
    const hasData = this.getEventsForTab(tab).length > 0;

    if (hasData) {
      // If there is data, ensure that it is not shown as loading
      this.setTabLoadingState(tab, false);
    } else {
      // If there is no data, show skeleton and make request
      this.setTabLoadingState(tab, true);
      this.fetchEvents(type);
    }
  }

  fetchEvents(type: 'full' | 'partial' | 'load-more' = 'full', onComplete?: () => void) {
    const tab = this.selectedTab();

    // only show loading if there is no data or it is a full load
    const hasData = this.getEventsForTab(tab).length > 0;
    const shouldShowFullLoading = type === 'full' && !hasData;

    this.fullLoading.set(shouldShowFullLoading);
    this.partialLoading.set(type === 'partial' && hasData);
    this.loadingMore.set(type === 'load-more');

    // Set individual tab loading state (except for load-more which uses loadingMore)
    if (type !== 'load-more') {
      this.setTabLoadingState(tab, true);
    }

    const name = this.filterByName();
    const business = this.filterByBusiness() ?? undefined;
    const page = this.currentPage();

    let request$;
    switch (tab) {
      case 'active':
        request$ = this.api.getUpcomingEventsBySeller(page, 12, name, business);
        break;
      case 'ended':
        request$ = this.api.getPastEventsBySeller(page, 12, name, business);
        break;
      case 'inactive':
        request$ = this.api.getInactiveEventsBySeller(page, 12, name, business);
        break;
    }

    request$.subscribe({
      next: (res: EventResponse) => {
        const newData = res.data;
        const currentData = this.currentEvents();

        switch (tab) {
          case 'active':
            this.upcomingEvents.set(page === 1 ? newData : [...currentData, ...newData]);
            break;
          case 'ended':
            this.pastEvents.set(page === 1 ? newData : [...currentData, ...newData]);
            break;
          case 'inactive':
            this.inactiveEvents.set(page === 1 ? newData : [...currentData, ...newData]);
            break;
        }

        this.totalPages.set(res.meta?.last_page ?? 1);

        this.totalPagesByTab[tab] = res.meta?.last_page ?? 1;

        this.lastFetchTimes[tab] = Date.now();

        // Reset individual tab loading state (except for load-more)
        if (type !== 'load-more') {
          this.setTabLoadingState(tab, false);
        }

        this.fullLoading.set(false);
        this.partialLoading.set(false);
        this.loadingMore.set(false);
        onComplete?.();
      },
      error: () => {
        // Reset individual tab loading state on error (except for load-more)
        if (type !== 'load-more') {
          this.setTabLoadingState(tab, false);
        }
        this.fullLoading.set(false);
        this.partialLoading.set(false);
        this.loadingMore.set(false);
        onComplete?.();
      }
    });
  }

  setSearchTerm(term: string) {
    this.filterByName.set(term);
    this.currentPage.set(1);
    this.fetchEvents('partial');
  }

  resetFilters(): void {
    this.filterByName.set('');
    this.filterByBusiness.set(null);
    this.currentPage.set(1);
  }

  resetAllState(): void {
    this.selectedTab.set('active');
    this.filterByName.set('');
    this.filterByBusiness.set(null);
    this.currentPage.set(1);
    this.upcomingEvents.set([]);
    this.pastEvents.set([]);
    this.inactiveEvents.set([]);
    this.lastFetchTimes.active = 0;
    this.lastFetchTimes.ended = 0;
    this.lastFetchTimes.inactive = 0;
  }

  loadEventWithTickets(eventId: number) {
    this.handleEventChange(eventId);

    forkJoin({
      event: this.api.getEventById(eventId, true, false),
      totals: this.eventReportApi.getKpis({
        eventId,
        role: 'seller',
        section: 'sales_home'
      })
    }).subscribe({
      next: ({ event, totals }) => {
        this.event.set(event);
        const data = totals?.data || {};
        this.totalTickets.set(data.total_tickets || 0);
        this.totalSales.set(data.total_amount || 0);
      },
      error: (err) => {
        console.error('Error loading event or ticket stats', err);
      }
    });
  }

  loadEvent(eventId: number) {
    this.handleEventChange(eventId);

    this.api.getEventById(eventId, true, false).subscribe({
      next: (event) => {
        this.event.set(event);
      },
      error: (err) => {
        console.error('Error loading event', err);
      }
    });
  }

  loadKpisSaleHomeTotals(eventId: number) {
    this.kpisLoading.set(true);

    this.eventReportApi.getKpis({
      eventId,
      role: 'seller',
      section: 'sales_home'
    }).subscribe({
      next: ({ data = {} }) => {
        this.totalTickets.set(data.total_tickets);
        this.totalSales.set(data.total_amount);
        this.kpisLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading ticket totals', err);
        this.kpisLoading.set(false);
      }
    });
  }

  loadSoldProductsKpisTotals(eventId: number) {
    this.kpisLoading.set(true);

    this.eventReportApi.getKpis({
      eventId,
      role: 'seller',
      section: 'my_sold_products'
    }).subscribe({
      next: ({ data = {} }) => {
        this.totalSales.set(data.total_amount || 0);
        this.totalEcommerceAmount.set(data.total_ecommerce_amount || 0);
        this.totalDirectAmount.set(data.total_direct_amount || 0);
        this.kpisLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading ticket totals', err);
        this.kpisLoading.set(false);
      }
    });
  }

  // Method to load only the current tab immediately
  loadCurrentTabOnly(type: 'full' | 'partial' = 'full', onComplete?: () => void) {
    const currentTab = this.selectedTab();

    // If the current tab already has valid data, don't load
    if (this.isCacheValid(currentTab)) {
      onComplete?.();
      return;
    }

    // If the current tab is already loading, do nothing
    if (this.getTabLoadingState(currentTab)) {
      return;
    }

    // Load only the current tab
    this.setTabLoadingState(currentTab, true);
    this.fetchEvents(type, onComplete);
  }

  /**
   * Loads page 1 of the active events for consumers that need them no matter
   * which tab the host screen shows (e.g. the event picker sheet opened from
   * the FAB). Unlike configureForActiveEvents() + loadCurrentTabOnly(), it never
   * touches selectedTab/currentPage, which belong to the host screen: on Inicio
   * that would flip a user browsing "Finalizados" over to "Activos".
   * Only loads when there is nothing to show, so it never replaces a list the
   * host screen has already paginated.
   */
  ensureActiveEventsLoaded(): void {
    if (this.upcomingEvents().length > 0 || this.activeTabLoading()) {
      return;
    }
    this.setTabLoadingState('active', true);
    this.loadTabsSequentially(['active'], this.filterByName(), this.filterByBusiness() ?? undefined, 1);
  }

  // Method to configure service for active events only
  configureForActiveEvents() {
    this.selectedTab.set('active');
    this.currentPage.set(1);
    this.totalPages.set(this.totalPagesByTab.active);
  }
}
